import { api } from 'api/client';
import { API_ENDPOINTS } from 'config/api.config';
import {
  ABTest,
  ABTestCreateRequest,
  ABTestUpdateRequest,
  ABTestListResponse,
  ABTestReport,
  ABTestRecipientsResponse,
  ABTestSendJob,
  ABAudiencePreview,
  ABAudiencePreviewRequest,
  ABMetric,
  ABTimePoint,
  RecipientQuery,
} from 'types/ab_test.types';
import { SuccessResponse } from 'types/api.types';

export const abTestApi = {
  list: async (
    appId: number,
    status?: string,
    limit = 50,
    offset = 0
  ): Promise<ABTestListResponse> => {
    const response = await api.get<ABTestListResponse>(API_ENDPOINTS.abTests.list, {
      app_id: appId,
      status: status || undefined,
      limit,
      offset,
    });
    return response.data;
  },

  get: async (id: number): Promise<ABTest> => {
    const response = await api.get<ABTest>(API_ENDPOINTS.abTests.get(id));
    return response.data;
  },

  create: async (
    data: ABTestCreateRequest
  ): Promise<SuccessResponse & { test: ABTest }> => {
    const response = await api.post<SuccessResponse & { test: ABTest }>(
      API_ENDPOINTS.abTests.create,
      data
    );
    return response.data;
  },

  update: async (id: number, data: ABTestUpdateRequest): Promise<SuccessResponse> => {
    const response = await api.put<SuccessResponse>(API_ENDPOINTS.abTests.update(id), data);
    return response.data;
  },

  duplicate: async (
    id: number,
    name?: string
  ): Promise<SuccessResponse & { test: ABTest }> => {
    const response = await api.post<SuccessResponse & { test: ABTest }>(
      API_ENDPOINTS.abTests.duplicate(id),
      { name }
    );
    return response.data;
  },

  delete: async (id: number): Promise<SuccessResponse> => {
    const response = await api.delete<SuccessResponse>(API_ENDPOINTS.abTests.delete(id));
    return response.data;
  },

  start: async (id: number): Promise<SuccessResponse> => {
    const response = await api.post<SuccessResponse>(API_ENDPOINTS.abTests.start(id));
    return response.data;
  },

  pause: async (id: number): Promise<SuccessResponse> => {
    const response = await api.post<SuccessResponse>(API_ENDPOINTS.abTests.pause(id));
    return response.data;
  },

  complete: async (
    id: number,
    winnerVariantId: number,
    metric: ABMetric = 'ctr'
  ): Promise<SuccessResponse> => {
    const response = await api.post<SuccessResponse>(API_ENDPOINTS.abTests.complete(id), {
      winner_variant_id: winnerVariantId,
      metric,
    });
    return response.data;
  },

  send: async (id: number): Promise<{ message: string; job: ABTestSendJob }> => {
    const response = await api.post<{ message: string; job: ABTestSendJob }>(
      API_ENDPOINTS.abTests.send(id)
    );
    return response.data;
  },

  sendStatus: async (id: number): Promise<ABTestSendJob | null> => {
    const response = await api.get<{ job: ABTestSendJob | null }>(
      API_ENDPOINTS.abTests.sendStatus(id)
    );
    return response.data.job;
  },

  getResults: async (id: number, metric: ABMetric = 'ctr'): Promise<ABTestReport> => {
    const response = await api.get<ABTestReport>(API_ENDPOINTS.abTests.results(id), {
      metric,
    });
    return response.data;
  },

  getTimeseries: async (
    id: number,
    bucket: 'hour' | 'day' = 'hour'
  ): Promise<ABTimePoint[]> => {
    const response = await api.get<{ points: ABTimePoint[] }>(
      API_ENDPOINTS.abTests.timeseries(id),
      { bucket }
    );
    return response.data.points || [];
  },

  getRecipients: async (
    id: number,
    query: RecipientQuery = {}
  ): Promise<ABTestRecipientsResponse> => {
    const response = await api.get<ABTestRecipientsResponse>(
      API_ENDPOINTS.abTests.recipients(id),
      {
        variant_id: query.variant_id || undefined,
        status: query.status || undefined,
        search: query.search || undefined,
        limit: query.limit ?? 50,
        offset: query.offset ?? 0,
      }
    );
    return response.data;
  },

  previewAudience: async (
    data: ABAudiencePreviewRequest
  ): Promise<ABAudiencePreview> => {
    const response = await api.post<ABAudiencePreview>(
      API_ENDPOINTS.abTests.previewAudience,
      data
    );
    return response.data;
  },

  exportRecipients: async (
    id: number,
    query: RecipientQuery & { sep?: ';' } = {},
    filename?: string
  ): Promise<void> => {
    const response = await api.get<string>(API_ENDPOINTS.abTests.recipientsExport(id), {
      variant_id: query.variant_id || undefined,
      status: query.status || undefined,
      search: query.search || undefined,
      sep: query.sep || undefined,
    });

    const blob = new Blob([response.data as unknown as BlobPart], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename ||
      `ab_test_${id}${query.variant_id ? `_variant_${query.variant_id}` : ''}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};